import { useEffect, useRef, useState } from "react";
import { Reveal } from "./Reveal";

const STATS = [
  { value: 8400, label: "Shows a year" },
  { value: 175, label: "Trained Hosts" },
  { value: 405000, label: "Annual participants" },
  { value: null, label: "Unlimited fun" },
];

const useCountUp = (to, start) => {
  const [n, setN] = useState(to);
  useEffect(() => {
    if (!start || to === null || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    setN(0);
    const t0 = performance.now(), dur = 1600; let raf;
    const tick = (t) => { const p = Math.min(1, (t - t0) / dur); setN(Math.round(to * (1 - Math.pow(1 - p, 3)))); if (p < 1) raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [to, start]);
  return n;
};

const Tile = ({ s, i, start, dark }) => {
  const n = useCountUp(s.value, start);
  return (
    <Reveal delay={0.1 * i} className={`rounded-2xl border p-5 text-center shadow-sm backdrop-blur sm:p-8 ${dark ? "border-white/10 bg-white/5" : "border-black/10 bg-white/80"}`} style={{ containerType: "inline-size" }} data-testid={`stat-tile-${i}`}>
      <p className={`whitespace-nowrap font-display leading-none tabular-nums ${dark ? "text-white" : "text-en-teal"}`} style={{ fontSize: "clamp(1.75rem, 18.5cqw, 4.5rem)" }}>
        {s.value === null ? (
          <span className="infinity-shine inline-block" aria-label="Unlimited" role="img">∞</span>
        ) : (
          <><span aria-hidden className="tabular-nums">{n.toLocaleString()}</span><span className="sr-only">{s.value.toLocaleString()}</span></>
        )}
      </p>
      <span aria-hidden className="mx-auto mt-4 block h-1 w-10 rounded-full bg-en-yellow" />
      <p className={`mt-3 font-gothic text-xs uppercase tracking-[0.2em] sm:text-sm ${dark ? "text-white/70" : "text-black/70"}`}>{s.label}</p>
    </Reveal>
  );
};

export const StatTiles = ({ dark = false, only }) => {
  const ref = useRef(null);
  const [start, setStart] = useState(false);
  useEffect(() => {
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setStart(true); io.disconnect(); } }, { threshold: 0.3 });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  const list = only ? STATS.filter((s) => only.includes(s.label)) : STATS;
  return (
    <div ref={ref} className={`grid grid-cols-2 gap-3 lg:gap-6 ${list.length === 3 ? "lg:grid-cols-3" : "lg:grid-cols-4"}`}>
      {list.map((s, i) => <Tile key={s.label} s={s} i={i} start={start} dark={dark} />)}
    </div>
  );
};

export const StatsBand = () => {
  return (
    <section data-testid="home-stats" className="relative isolate overflow-hidden border-t border-black/10 bg-white py-20 lg:py-28">
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="hero-blob absolute left-[-10%] top-[-30%] h-[50vw] max-h-[520px] w-[50vw] max-w-[520px] rounded-full bg-en-yellow/15 blur-3xl" />
        <div className="hero-blob absolute bottom-[-40%] right-[-8%] h-[50vw] max-h-[520px] w-[50vw] max-w-[520px] rounded-full bg-en-teal/10 blur-3xl" style={{ animationDelay: "-7s" }} />
        <svg viewBox="0 0 24 24" className="glow-star absolute right-[6%] top-6 hidden w-9 text-en-yellow md:block" style={{ "--d": "-1s", "--t": "3.4s" }}><path d="M12 1.5 C12.8 7.5 16.5 11.2 22.5 12 C16.5 12.8 12.8 16.5 12 22.5 C11.2 16.5 7.5 12.8 1.5 12 C7.5 11.2 11.2 7.5 12 1.5 Z" fill="currentColor" /></svg>
        <svg viewBox="0 0 24 24" className="glow-star absolute bottom-8 left-[4%] hidden w-6 text-en-yellow md:block" style={{ "--d": "-2.4s", "--t": "2.9s" }}><path d="M12 1.5 C12.8 7.5 16.5 11.2 22.5 12 C16.5 12.8 12.8 16.5 12 22.5 C11.2 16.5 7.5 12.8 1.5 12 C7.5 11.2 11.2 7.5 12 1.5 Z" fill="currentColor" /></svg>
      </div>
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <p className="eyebrow mb-4">Trusted experience</p>
          <h2 className="font-display text-3xl leading-[1.05] text-black sm:text-4xl lg:text-5xl">Experience you can count on.</h2>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-black/70 sm:text-lg">Thousands of live shows a year, hosted by pros who do this every week.</p>
        </Reveal>
        <div className="mt-12"><StatTiles /></div>
        <Reveal delay={0.4}><p className="mt-8 text-sm text-black/55" data-testid="stats-attribution">Across the Trivia Nation family — the team behind Entertainment Nation.</p></Reveal>
      </div>
    </section>
  );
};
